import {
  Box,
  Button,
  Typography,
  Pagination,
  CircularProgress,
  Breadcrumbs,
  Link,
  Tooltip,
  styled,
} from "@mui/material";
import React, { useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CustomInput, PageTitle } from "../../components-new/common";
import { useFetchTestCasesInSuite, useSearchInQuery } from "../../useQuery";
import TestSuiteCases from "./TestSuiteCases";
import { transformPaginatedTestCases } from "./suiteUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const StyledButton = styled(Button)({
  height: "55px",
  textTransform: "capitalize",
  boxShadow: "none",
  minWidth: "100px",
});

const TestSuiteDetail = () => {
  const { suiteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const productId = searchParams.get("productId");
  const productName = searchParams.get("productName") || "Product";
  const suiteName = searchParams.get("suiteName") || "Suite";
  const parentSuiteId = searchParams.get("parentSuiteId");
  const parentSuiteName = searchParams.get("parentSuiteName");

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearched, setIsSearched] = useState(false);

  const {
    data: testCasesInSuite,
    isLoading,
    refetch,
  } = useFetchTestCasesInSuite(suiteId, currentPage);

  const {
    refetchWithParams: fetchSearchInSuite,
    data: searchData,
    isLoading: isSearching,
  } = useSearchInQuery();

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setIsSearched(true);
      fetchSearchInSuite({
        pnum: 1,
        key: searchQuery,
        suiteid: suiteId,
      });
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearched(false);
    refetch();
  };

  const handleNestedSuiteClick = (nestedSuiteId, nestedSuiteName) => {
    const params = new URLSearchParams({
      productId: productId || "",
      productName: productName,
      suiteName: nestedSuiteName || "Suite",
      parentSuiteId: suiteId,
      parentSuiteName: suiteName,
    });
    navigate(`/new/test-suites/suite/${nestedSuiteId}?${params.toString()}`);
  };

  const handleBackClick = () => {
    if (parentSuiteId) {
      const params = new URLSearchParams({
        productId: productId || "",
        productName: productName,
        suiteName: parentSuiteName || "Suite",
      });
      navigate(`/new/test-suites/suite/${parentSuiteId}?${params.toString()}`);
    } else {
      navigate("/new/test-suites", {
        state: { productId: productId, productName: productName },
      });
    }
  };

  const transformedData = transformPaginatedTestCases(testCasesInSuite);
  const totalPages = testCasesInSuite?.[0]?.list?.[0]?.totalPages || 1;

  const displayData = isSearched ? searchData || [] : transformedData;

  return (
    <>
      <Box sx={{ mt: 2, mb: 3 }} display="flex" alignItems="center" gap={2}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{
            textTransform: "capitalize",
            borderColor: "#E2E6F3",
            color: "#2B3539",
          }}
        >
          Back
        </Button>

        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={() => navigate("/new/test-suites", { state: { productId, productName } })}
          >
            Test Suites
          </Link>
          {parentSuiteName && (
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                const params = new URLSearchParams({
                  productId: productId || "",
                  productName: productName,
                  suiteName: parentSuiteName,
                });
                navigate(`/new/test-suites/suite/${parentSuiteId}?${params.toString()}`);
              }}
            >
              {parentSuiteName}
            </Link>
          )}
          <Typography color="text.primary">{suiteName}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 3 }} display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <PageTitle title={suiteName} />
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Application: {productName}
          </Typography>
        </Box>

        <Box display="flex" alignItems="flex-end" columnGap={2}>
          <CustomInput
            labelinput={`Search in "${suiteName}"`}
            placeholder="Search test case"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            name="searchInput"
          />
          <Tooltip title={`Search in "${suiteName}"`} arrow>
            <StyledButton
              variant="contained"
              disabled={isSearching}
              onClick={handleSearch}
              sx={{ backgroundColor: "#5541D7" }}
              startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSearching ? "Searching..." : "Search"}
            </StyledButton>
          </Tooltip>
          {isSearched && searchQuery && (
            <Button
              variant="outlined"
              size="medium"
              onClick={handleClearSearch}
              sx={{
                height: "55px",
                textTransform: "capitalize",
                borderColor: "#5541D7",
                color: "#5541D7",
                whiteSpace: "nowrap",
              }}
            >
              Clear: "{searchQuery}"
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TestSuiteCases
            data={displayData}
            itemsPerRow={4}
            showTestCases={true}
            testSuiteId={suiteId}
            setShowTestCases={() => {}}
            selectedProdId={productId}
            titleSuiteName={suiteName}
            showDeletedTestCases={false}
            handleNestedSuiteClick={(nestedSuiteId) => {
              const suite = displayData.find((s) => s.idtest_suite === nestedSuiteId);
              handleNestedSuiteClick(nestedSuiteId, suite?.Test_Suite);
            }}
            isSearched={isSearched}
          />

          {!isSearched && (
            <Box py={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, newPage) => setCurrentPage(newPage)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default TestSuiteDetail;
